import GameState from "./game-state";

/**
 * Change level state - changes background and palette, handles fade transition
 * Reference: quadra/source/player.cc:920-943 (Player_change_level)
 */
export default class ChangeLevelState extends GameState {
  private fadeFrame: number = 0;
  private readonly fadeFrames: number = 16; // Matches original nframe=16
  private fadePhase: "toBlack" | "fromBlack" = "toBlack";
  private waitFrames: number = 0;
  private readonly waitFramesMax: number = 14; // Matches original Wait_time(14)

  enter(): void {
    // Reference: quadra/source/player.cc:928-942 (Player_change_level::init)
    // Change level (background and palette)
    this.changeLevel();
    
    // Note: The original calls call(new Fade_to(...)) and call(new Wait_time(14)) then ret()
    // Since we're in a parallel executor (called from LevelUpState), we can run the fade
    // sequence here. The original ret() happens after setting up the fade, so we don't
    // pop immediately - we run the fade sequence and then mark as done.
    // Start fade to black
    this.fadePhase = "toBlack";
    this.fadeFrame = 0;
  }

  update(frameTime: number): void {
    super.update(frameTime);
    
    if (this.fadePhase === "toBlack") {
      this.fadeFrame++;
      if (this.fadeFrame >= this.fadeFrames) {
        // Fade to black complete, wait a bit
        this.waitFrames++;
        if (this.waitFrames >= this.waitFramesMax) {
          // Start fade from black
          this.fadePhase = "fromBlack";
          this.fadeFrame = 0;
        }
      }
    } else if (this.fadePhase === "fromBlack") {
      this.fadeFrame++;
      if (this.fadeFrame >= this.fadeFrames) {
        // Fade complete, mark as done (executor will remove us)
        this.markDone();
      }
    }
  }
  
  /**
   * Mark state as done when fade completes
   */
  isDone(): boolean {
    return this.fadePhase === "fromBlack" && this.fadeFrame >= this.fadeFrames;
  }

  /**
   * Change level - update background and palette
   * Reference: quadra/source/canvas.cc:650-657 (change_level_single)
   * Reference: quadra/source/canvas.cc:659-695 (change_level)
   */
  private changeLevel(): void {
    const level = this.canvas.level;
    // Calculate which background to use: (level-1) % 10
    // Cycles through fond0.png to fond9.png
    const backgroundIndex = (level - 1) % 10;
    
    // Background images are in s../core/engine/images/
    // Import them statically based on index
    const backgroundImports = [
      require("../images/fond0.png"),
      require("../images/fond1.png"),
      require("../images/fond2.png"),
      require("../images/fond3.png"),
      require("../images/fond4.png"),
      require("../images/fond5.png"),
      require("../images/fond6.png"),
      require("../images/fond7.png"),
      require("../images/fond8.png"),
      require("../images/fond9.png"),
    ];
    
    const backgroundPath = backgroundImports[backgroundIndex];
    
    // Load image and extract palette
    // Reference: quadra/source/canvas.cc:683-687 (load image, extract palette)
    this.loadBackgroundAndUpdatePalette(backgroundPath);
  }

  /**
   * Load background image and update palette
   */
  private loadBackgroundAndUpdatePalette(imagePath: string): void {
    const img = new Image();
    img.onload = () => {
      // Extract colors from image to update palette
      // Reference: quadra/source/canvas.cc:685-687 (extract palette from image)
      // For now, we'll trigger a palette update event that the view can handle
      // The actual palette extraction would need to be implemented in the view
      console.log(`[ChangeLevelState] Background changed to ${imagePath} for level ${this.canvas.level}`);
      
      // Store background path in game for view to use
      (this.game as any).currentBackground = imagePath;
      (this.game as any).backgroundChanged = true;
    };
    img.onerror = () => {
      console.error(`[ChangeLevelState] Failed to load background: ${imagePath}`);
      // Continue anyway - mark as done
      this.markDone();
    };
    img.src = imagePath;
  }

  paint() {
    // Draw fade overlay
    if (this.fadePhase === "toBlack") {
      // Draw black overlay with alpha
      // This would be handled by the view
      // Alpha calculation: this.fadeFrame / this.fadeFrames
    } else if (this.fadePhase === "fromBlack") {
      // Draw black overlay with alpha
      // Alpha calculation: 1 - (this.fadeFrame / this.fadeFrames)
    }
  }

  toString(): string {
    return "Change Level";
  }
}

